import { useMutation, useQueryClient } from '@tanstack/react-query';
import { idempotencyKeyFor } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { GradeDiscountExclusions, GradeItem } from './useUserGradesQuery';

export interface GradeCreateInput {
  gradeCode: string;
  gradeName: string;
  priority: number;
  /** 등급 일괄 할인율(%) 0~100. R6 O 기업회원 등급가. 미지정 시 BE DEFAULT 0. */
  discountRate?: number | undefined;
  /** 등급 추가 적립률(%) 0~100. B2B(2026-06-20). 미지정 시 BE DEFAULT 0. */
  mileageRate?: number | undefined;
  /** 무통장입금 허용 여부. 미지정 시 BE DEFAULT TRUE(현행 유지). */
  allowBankTransfer?: boolean | undefined;
}

export interface GradePatchInput {
  gradeId: string;
  gradeName?: string | undefined;
  priority?: number | undefined;
  active?: boolean | undefined;
  /** 등급 할인율(%) 0~100. undefined=미변경(PATCH 부분수정). */
  discountRate?: number | undefined;
  /** 등급 추가 적립률(%) 0~100. undefined=미변경(PATCH 부분수정). */
  mileageRate?: number | undefined;
  /** 무통장입금 허용 여부. undefined=미변경(PATCH 부분수정). */
  allowBankTransfer?: boolean | undefined;
  /** QA-464 할인 제외 — 보내면 네 목록을 통째로 바꾼다. undefined=미변경. */
  discountExclusions?: GradeDiscountExclusions | undefined;
}

const call = apiClient.request as unknown as (...a: unknown[]) => Promise<[unknown, unknown]>;

export const useUserGradeCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation<GradeItem, Error, GradeCreateInput>({
    mutationFn: async (body) => {
      const key = idempotencyKeyFor('POST', '/api/admin/v1/user-grades', body);
      const [data] = await call('post', '/api/admin/v1/user-grades', {
        body,
        params: { header: { 'Idempotency-Key': key } },
      });
      return data as GradeItem;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'user-grades'] });
    },
  });
};

export const useUserGradePatchMutation = () => {
  const qc = useQueryClient();
  return useMutation<GradeItem, Error, GradePatchInput>({
    mutationFn: async ({ gradeId, ...body }) => {
      const key = idempotencyKeyFor('PATCH', `/api/admin/v1/user-grades/${gradeId}`, body);
      const [data] = await call('patch', '/api/admin/v1/user-grades/{gradeId}', {
        body,
        params: {
          path: { gradeId },
          header: { 'Idempotency-Key': key },
        },
      });
      return data as GradeItem;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'user-grades'] });
    },
  });
};

export const useUserGradeDeleteMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (gradeId) => {
      await call('delete', '/api/admin/v1/user-grades/{gradeId}', {
        params: { path: { gradeId } },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'user-grades'] });
    },
  });
};
